import React from 'react';
import {Form} from 'react-bootstrap';
import Select, {GroupBase} from 'react-select';
import {OptionsOrGroups, SingleValue} from 'react-select/dist/declarations/src/types';

export interface SelectOption {
  value: any;
  label: string;
  isDisabled?: boolean;
  description?: string;
}

export interface SelectFieldProps {
  id?: string;
  name: string;
  label?: string;
  value: SelectOption | null;
  onChange: (value: SelectOption | null) => void;
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  error?: string;
  touched?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  customStyles?: any;
  compact?: boolean;
  maxMenuHeight?: number;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  menuPortalTarget?: HTMLElement;
  menuShouldScrollIntoView?: boolean;
  formatOptionLabel?: (option: SelectOption, meta: any) => React.ReactNode;
  formatGroupLabel?: (group: GroupBase<SelectOption>) => React.ReactNode;
  filterOption?: any;
  'data-cy'?: string;
}

const SelectField: React.FC<SelectFieldProps> = ({
    id,
    name,
    label,
    value,
    onChange,
    options,
    placeholder,
    isDisabled = false,
    isRequired = false,
    error,
    touched,
    isClearable = false,
    isSearchable = true,
    customStyles,
    compact = false,
    maxMenuHeight,
    menuPlacement,
    menuPortalTarget,
    menuShouldScrollIntoView,
    formatOptionLabel,
    formatGroupLabel,
    filterOption,
    'data-cy': dataCy,
}) => {
    const handleChange = (selectedValue: SingleValue<SelectOption>) => {
        onChange(selectedValue);
    };

    return (
        <Form.Group className={compact ? 'mb-0' : 'mb-3'} controlId={id || name} >
            {
                label && (
                    <Form.Label>
                        {label}
                        {isRequired && <span className="text-danger"> *</span>}
                    </Form.Label>
                )
            }

            <div className={compact ? 'mb-0' : 'mb-3'}>
                <Select
                    id={id}
                    name={name}
                    value={value}
                    onChange={handleChange}
                    options={options}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    styles={customStyles}
                    maxMenuHeight={maxMenuHeight}
                    menuPlacement={menuPlacement}
                    menuPortalTarget={menuPortalTarget}
                    menuShouldScrollIntoView={menuShouldScrollIntoView}
                    formatOptionLabel={formatOptionLabel}
                    formatGroupLabel={formatGroupLabel}
                    filterOption={filterOption}
                    data-cy={dataCy}
                />
            </div>
            {
                touched && error && (
                    <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                        {error}
                    </Form.Control.Feedback>
                )
            }
        </Form.Group >
    );
};

export default SelectField;
